import { isRedirect, redirect } from '@tanstack/react-router'

import * as Sentry from '@sentry/react'

import { API_URL } from '@/lib/constants'

import { isAbortError as checkAbortError } from '@/shared/api/utils/error'
import { getCurrentTenant } from '@/shared/auth/tenant-store'
import { getErrorContext } from '@/shared/error-context/store'

import { getAuthToken } from './auth'
import { confirmationService } from './confirmation-service'

import type { ApiError } from '@/shared/api/utils/error'
import type { ConfirmationResponse } from '@/shared/confirmation/types'

interface ApiOptions extends Omit<RequestInit, 'body' | 'headers'> {
	params?: Record<string, string | number | boolean>
	headers?: Record<string, string>
	tenants?: string[]
}

/**
 * HTTP client wrapper for AIR® backend services. Threads Clerk auth, tenant
 * scoping, and Sentry trace headers into every request; retries a single 401
 * with a forced token refresh before redirecting to logout.
 *
 * Used as a singleton for the main AIR® REST API (`baseURL = API_URL` by
 * default) and instantiated separately for sibling services that live on
 * other hostnames (e.g. the Noah AI backend — see `src/features/ai/api.ts`).
 *
 * @param baseURL Backend root — defaults to `API_URL`. Pass a different host
 *                to create a parallel client for that service.
 */
export class ApiClient {
	private baseURL: string
	private MAX_AUTH_RETRIES = 2

	constructor(baseURL: string = API_URL) {
		this.baseURL = baseURL
	}

	private getSentryHeaders(): Record<string, string | undefined> {
		const traceData = Sentry.getTraceData()
		const sentryTraceHeader = traceData['sentry-trace']
		const sentryBaggageHeader = traceData['baggage']

		return { sentryTraceHeader, sentryBaggageHeader }
	}

	private createApiError(
		message: string,
		response?: Response,
		data?: unknown,
	): ApiError {
		const error = new Error(message) as ApiError
		;(error as { _isApiError: true })._isApiError = true
		error.status = response?.status
		error.data = data
		return error
	}

	/**
	 * Extract error message from common API response patterns
	 */
	private extractErrorMessage(data: unknown): string | undefined {
		if (!data || typeof data !== 'object') return undefined
		for (const key of ['message', 'error', 'detail'] as const) {
			const val = (data as Record<string, unknown>)[key]
			if (!val) continue
			if (typeof val === 'string') return val
			if (typeof val === 'object' && val && 'message' in val)
				return String((val as { message: unknown }).message)
			return JSON.stringify(val)
		}
		return undefined
	}

	/**
	 * Type guard to check if data is a valid ConfirmationResponse
	 */
	private isConfirmationResponse(data: unknown): data is ConfirmationResponse {
		return (
			data !== null &&
			typeof data === 'object' &&
			'confirmation_id' in data &&
			'warning' in data &&
			'expires' in data &&
			typeof (data as ConfirmationResponse).confirmation_id === 'string' &&
			typeof (data as ConfirmationResponse).warning === 'string'
		)
	}

	private async request<T>(
		endpoint: string,
		method: string,
		options: ApiOptions & { body?: unknown } = {},
		retryCount: number = 0,
	): Promise<T> {
		const startTime = performance.now()
		const {
			params,
			body,
			signal,
			headers = {},
			tenants,
			...fetchOptions
		} = options

		// Build URL with query params
		const url = new URL(`${this.baseURL}${endpoint}`)
		if (params) {
			Object.entries(params).forEach(([key, value]) => {
				url.searchParams.append(key, String(value))
			})
		}

		// Build headers
		const requestHeaders: Record<string, string> = {
			'Content-Type': 'application/json',
			...headers,
		}

		// Get auth token (force fresh on retry)
		const token = await getAuthToken(
			retryCount > 0 ? { skipCache: true } : undefined,
		)

		// Get Sentry headers
		const { sentryTraceHeader, sentryBaggageHeader } = this.getSentryHeaders()

		if (sentryTraceHeader) {
			requestHeaders['sentry-trace'] = sentryTraceHeader
		}

		if (sentryBaggageHeader) {
			requestHeaders['baggage'] = sentryBaggageHeader
		}

		if (token) {
			requestHeaders['Authorization'] = `Bearer ${token}`
		}

		// Tenant header — use explicit tenants array or fallback to current tenant
		const resolvedTenants = tenants ?? [getCurrentTenant()].filter(Boolean)
		if (resolvedTenants.length > 0) {
			requestHeaders['X-AIR-Request-Tenants'] = resolvedTenants.join(',')
		}

		const requestBody = body ? JSON.stringify(body) : undefined

		try {
			const response = await fetch(url.toString(), {
				...fetchOptions,
				method,
				headers: requestHeaders,
				body: requestBody,
				signal,
			})

			if (!response.ok) {
				let errorData: unknown
				let errorMessage = `${response.status}${response.statusText ? ` ${response.statusText}` : ''}`

				try {
					const responseText = await response.text()
					if (responseText) {
						try {
							errorData = JSON.parse(responseText)
							errorMessage = this.extractErrorMessage(errorData) ?? errorMessage
						} catch {
							errorMessage = responseText
							errorData = { message: responseText }
						}
					}
				} catch {
					// Failed to read response body — keep status-based message
				}

				const error = this.createApiError(errorMessage, response, errorData)

				// Handle 401 — retry with fresh token, then redirect to logout
				if (response.status === 401) {
					if (retryCount < this.MAX_AUTH_RETRIES) {
						return this.request<T>(endpoint, method, options, retryCount + 1)
					}

					Sentry.addBreadcrumb({
						category: 'auth',
						message: 'Auth retries exhausted, redirecting to logout',
						level: 'info',
					})
					throw redirect({ to: '/logout' })
				}

				// Handle 449 (Confirmation Required)
				if (response.status === 449 && this.isConfirmationResponse(errorData)) {
					try {
						const confirmationHeaders =
							await confirmationService.requestConfirmation(errorData)

						const retryHeaders: Record<string, string> = {
							...headers,
							'X-AIR-confirm': confirmationHeaders['X-AIR-confirm'],
						}

						if (confirmationHeaders['X-AIR-confirm-passphrase']) {
							retryHeaders['X-AIR-confirm-passphrase'] =
								confirmationHeaders['X-AIR-confirm-passphrase']
						}

						return this.request<T>(endpoint, method, {
							...options,
							headers: retryHeaders,
						})
					} catch {
						throw error
					}
				}

				Sentry.addBreadcrumb({
					category: 'api',
					message: `${method} ${endpoint} ${response.status}`,
					level: response.status >= 500 ? 'error' : 'warning',
					data: {
						method,
						endpoint,
						status: response.status,
						duration: Math.round(performance.now() - startTime),
						errorMessage,
						requestKeys: body ? Object.keys(body as object) : undefined,
						...getErrorContext(),
					},
				})

				throw error
			}

			Sentry.addBreadcrumb({
				category: 'api',
				message: `${method} ${endpoint} ${response.status}`,
				level: 'info',
				data: {
					method,
					endpoint,
					status: response.status,
					duration: Math.round(performance.now() - startTime),
					...getErrorContext(),
				},
			})

			// Parse the successful body once, then hand it back.
			let result: T
			if (
				// Handle empty responses (204 No Content)
				response.status === 204 ||
				response.headers.get('content-length') === '0'
			) {
				result = {} as T
			} else {
				const contentType = response.headers.get('content-type') || ''
				result = contentType.includes('application/json')
					? ((await response.json()) as T)
					: ((await response.text()) as T)
			}

			// Dev-only wire-DTO capture (ADR 0007 / issue 01e). The guard is a
			// build-time literal (`vite.config.ts` folds `VITE_CAPTURE`), so this
			// branch and the dynamically-imported tool tree-shake out of every
			// normal build. Fire-and-forget: capture must never affect the request.
			if (import.meta.env.VITE_CAPTURE === 'true') {
				void import('./api-capture')
					.then((m) =>
						m.recordCapture({
							method,
							endpoint,
							params,
							requestBody: body,
							status: response.status,
							response: result,
						}),
					)
					.catch(() => {})
			}

			return result
		} catch (error) {
			// Re-throw API errors
			if (error instanceof Error && '_isApiError' in error) {
				throw error
			}

			// Abort errors are expected (user navigation, etc)
			if (checkAbortError(error)) {
				throw error
			}

			// Re-throw redirects from 401 handling
			if (isRedirect(error)) {
				throw error
			}

			// Wrap network/fetch errors
			const networkError = this.createApiError(
				error instanceof Error ? error.message : 'Network error',
			)

			Sentry.addBreadcrumb({
				category: 'api',
				message: `${method} ${endpoint} NETWORK_ERROR`,
				level: 'error',
				data: {
					method,
					endpoint,
					duration: Math.round(performance.now() - startTime),
					errorMessage:
						error instanceof Error ? error.message : 'Network error',
					...getErrorContext(),
				},
			})

			throw networkError
		}
	}

	async get<T>(endpoint: string, options?: ApiOptions): Promise<{ data: T }> {
		const data = await this.request<T>(endpoint, 'GET', options)
		return { data }
	}

	async post<T>(
		endpoint: string,
		data?: unknown,
		options?: ApiOptions,
	): Promise<{ data: T }> {
		const result = await this.request<T>(endpoint, 'POST', {
			...options,
			body: data,
		})
		return { data: result }
	}

	async put<T>(
		endpoint: string,
		data?: unknown,
		options?: ApiOptions,
	): Promise<{ data: T }> {
		const result = await this.request<T>(endpoint, 'PUT', {
			...options,
			body: data,
		})
		return { data: result }
	}

	async patch<T>(
		endpoint: string,
		data?: unknown,
		options?: ApiOptions,
	): Promise<{ data: T }> {
		const result = await this.request<T>(endpoint, 'PATCH', {
			...options,
			body: data,
		})
		return { data: result }
	}

	async delete<T>(
		endpoint: string,
		options?: ApiOptions & { data?: unknown },
	): Promise<{ data: T }> {
		const { data, ...restOptions } = options || {}
		const result = await this.request<T>(endpoint, 'DELETE', {
			...restOptions,
			body: data,
		})
		return { data: result }
	}
}

// Export singleton instance
export const api = new ApiClient()
